import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Eye, BookOpen, Plus, Briefcase, Sparkles, Calendar, Heart } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WeeklyHighlights = ({ user }) => {
  const [highlights, setHighlights] = useState([]);
  const [myJournal, setMyJournal] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showJournalDialog, setShowJournalDialog] = useState(false);
  const [selectedHighlight, setSelectedHighlight] = useState(null);
  const [newHighlight, setNewHighlight] = useState({ 
    content: '', 
    category: 'experience',
    mood: '😊'
  });

  const categories = [
    { id: 'experience', label: 'Work Experience', icon: '💼', color: 'bg-blue-500' },
    { id: 'learned', label: 'Something I Learned', icon: '💡', color: 'bg-yellow-500' },
    { id: 'challenge', label: 'Challenge I Faced', icon: '💪', color: 'bg-orange-500' },
    { id: 'achievement', label: 'Achievement', icon: '🏆', color: 'bg-green-500' },
    { id: 'grateful', label: 'Grateful For', icon: '🙏', color: 'bg-purple-500' },
  ];

  const moods = ['😊', '🔥', '💯', '😤', '🤔', '😎', '🙌', '💪'];

  useEffect(() => {
    fetchHighlights();
    fetchMyJournal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchHighlights = async () => {
    try {
      const response = await fetch(`${API_URL}/api/highlights/group`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHighlights(data);
      }
    } catch (error) {
      console.error('Error fetching highlights:', error);
    }
  };

  const fetchMyJournal = async () => {
    try {
      const response = await fetch(`${API_URL}/api/highlights/my-journal`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMyJournal(data);
      }
    } catch (error) {
      console.error('Error fetching journal:', error);
    }
  };

  const handleCreateHighlight = async () => {
    if (!newHighlight.content.trim()) {
      toast.error('Write something about your week!');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/highlights`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newHighlight)
      });

      if (response.ok) {
        toast.success('Highlight added! 🎉');
        setShowCreateDialog(false);
        setNewHighlight({ content: '', category: 'experience', mood: '😊' });
        fetchHighlights();
        fetchMyJournal();
      }
    } catch (error) {
      toast.error('Failed to post highlight');
    }
  };

  const likeHighlight = async (highlightId) => {
    try {
      await fetch(`${API_URL}/api/highlights/${highlightId}/like`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      fetchHighlights();
    } catch (error) {
      console.error('Error liking highlight:', error);
    }
  };

  const getCategoryInfo = (categoryId) => {
    return categories.find(c => c.id === categoryId) || categories[0];
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <>
      {/* Work Journal Section */}
      <div className="space-y-4">
        {/* Header with Description */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl font-bold flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-500" />
              Work Journal
            </h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowJournalDialog(true)}
              className="rounded-full text-xs"
              data-testid="view-journal-btn"
            >
              <Calendar className="w-4 h-4 mr-1" />
              My Wins
            </Button>
          </div>
          {/* Fun description for teens */}
          <p className="text-sm text-muted-foreground">
            🔥 Drop your wins! Did something cool at work? Learned something new? Flex it here and earn points!
          </p>
        </div>

        {/* Highlights Horizontal Scroll */}
        <div className="flex gap-3 overflow-x-auto pb-4 px-1 snap-x snap-mandatory" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
          {/* Add Highlight Button - Fun design */}
          <motion.button
            data-testid="add-highlight-button"
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowCreateDialog(true)}
            className="flex-shrink-0 snap-start"
          >
            <Card className="w-44 h-48 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-yellow-400/20 to-orange-400/20 border-2 border-dashed border-yellow-400/50 hover:border-yellow-500 hover:shadow-lg transition-all rounded-2xl">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-lg">
                <Plus className="w-8 h-8 text-white" />
              </div>
              <div className="text-center">
                <p className="font-bold text-sm">Drop a Win 🏆</p>
                <p className="text-xs text-muted-foreground">+10 pts per highlight</p>
              </div>
            </Card>
          </motion.button>

          {/* Highlight Cards */}
          {highlights.map((highlight) => {
            const category = getCategoryInfo(highlight.category);
            const isLiked = highlight.likes?.includes(user?.id);
            
            return (
              <motion.div
                key={highlight.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex-shrink-0 snap-start"
              >
                <Card 
                  className="w-40 h-48 p-3 flex flex-col cursor-pointer hover:shadow-lg transition-all rounded-2xl overflow-hidden"
                  onClick={() => setSelectedHighlight(highlight)}
                  data-testid={`highlight-${highlight.id}`}
                >
                  {/* Category Badge */}
                  <Badge className={`${category.color} text-white text-xs w-fit mb-2`}>
                    {category.icon} {category.label}
                  </Badge>
                  
                  {/* Content Preview */}
                  <p className="text-sm flex-1 line-clamp-4 text-foreground/80">
                    {highlight.content}
                  </p>
                  
                  {/* Footer */}
                  <div className="mt-2 pt-2 border-t">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <span className="text-lg">{highlight.user_avatar || '😊'}</span>
                        <span className="text-xs text-muted-foreground truncate max-w-[60px]">
                          {highlight.user_name?.split(' ')[0]}
                        </span>
                      </div>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          likeHighlight(highlight.id);
                        }}
                        className={`flex items-center gap-1 text-xs ${isLiked ? 'text-red-500' : 'text-muted-foreground'}`}
                      >
                        <Heart className={`w-3 h-3 ${isLiked ? 'fill-current' : ''}`} />
                        {highlight.likes?.length || 0}
                      </button>
                    </div>
                  </div>
                </Card>
              </motion.div>
            );
          })}

          {highlights.length === 0 && (
            <Card className="w-64 h-48 flex items-center justify-center text-center p-4 rounded-2xl">
              <div>
                <Sparkles className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No highlights yet this week</p>
                <p className="text-xs text-muted-foreground">Be the first to share!</p>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Create Highlight Dialog - Teen-friendly */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md rounded-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-yellow-500" />
              Drop Your Win! 🔥
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pb-4">
            {/* Category Selection */}
            <div>
              <label className="text-sm font-semibold mb-2 block">What's the vibe? 🎯</label>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setNewHighlight({ ...newHighlight, category: cat.id })}
                    className={`p-3 rounded-xl text-left transition-all ${
                      newHighlight.category === cat.id 
                        ? 'bg-gradient-to-br from-yellow-100 to-orange-100 ring-2 ring-yellow-400' 
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <span className="text-xl">{cat.icon}</span>
                    <p className="text-xs font-medium mt-1">{cat.label}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Mood Selection */}
            <div>
              <label className="text-sm font-semibold mb-2 block">How you feelin? 😎</label>
              <div className="flex gap-1 flex-wrap">
                {moods.map((mood) => (
                  <button
                    key={mood}
                    onClick={() => setNewHighlight({ ...newHighlight, mood })}
                    className={`text-xl p-1.5 rounded-lg transition-all ${
                      newHighlight.mood === mood 
                        ? 'bg-gradient-to-br from-yellow-100 to-orange-100 ring-2 ring-yellow-400 scale-110' 
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    {mood}
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <div>
              <label className="text-sm font-semibold mb-2 block">Spill the tea ☕</label>
              <Textarea
                data-testid="highlight-content-input"
                value={newHighlight.content}
                onChange={(e) => setNewHighlight({ ...newHighlight, content: e.target.value })}
                placeholder="What's the W? Did you crush it at work? Learn something fire? Help someone out? Tell us! 🙌"
                className="min-h-[80px] rounded-2xl text-base"
                maxLength={500}
              />
              <p className="text-xs text-muted-foreground mt-1">{newHighlight.content.length}/500</p>
            </div>

            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-2 text-sm">
              <p className="font-semibold text-orange-800">💰 +10 points for sharing!</p>
            </div>

            <Button
              data-testid="post-highlight-button"
              onClick={handleCreateHighlight}
              className="w-full h-11 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 font-bold text-white"
            >
              Post My Win 🚀
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Highlight Detail Dialog */}
      <Dialog open={!!selectedHighlight} onOpenChange={() => setSelectedHighlight(null)}>
        <DialogContent className="sm:max-w-md rounded-3xl max-h-[85vh] overflow-y-auto">
          {selectedHighlight && (
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-2xl">
                  {selectedHighlight.user_avatar || '😊'}
                </div>
                <div>
                  <p className="font-bold">{selectedHighlight.user_name}</p>
                  <p className="text-xs text-muted-foreground">{formatDate(selectedHighlight.created_at)}</p>
                </div>
                <Badge className={`${getCategoryInfo(selectedHighlight.category).color} text-white ml-auto`}>
                  {getCategoryInfo(selectedHighlight.category).icon}
                </Badge>
              </div>

              {/* Content */}
              <div className="bg-gray-50 rounded-2xl p-4">
                <div className="text-3xl mb-3">{selectedHighlight.mood}</div>
                <p className="text-foreground leading-relaxed">{selectedHighlight.content}</p>
              </div>

              {/* Likes */}
              <div className="flex items-center justify-between">
                <button 
                  onClick={() => likeHighlight(selectedHighlight.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                    selectedHighlight.likes?.includes(user?.id)
                      ? 'bg-red-100 text-red-600'
                      : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  <Heart className={`w-5 h-5 ${selectedHighlight.likes?.includes(user?.id) ? 'fill-current' : ''}`} />
                  <span className="font-semibold">{selectedHighlight.likes?.length || 0}</span>
                  <span className="text-sm">Likes</span>
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* My Journal Dialog */}
      <Dialog open={showJournalDialog} onOpenChange={setShowJournalDialog}>
        <DialogContent className="sm:max-w-lg rounded-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-primary" />
              My Work Journal
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {myJournal.length === 0 ? (
              <div className="text-center py-8">
                <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Your journal is empty</p>
                <p className="text-sm text-muted-foreground">Start documenting your summer work experience!</p>
                <Button 
                  onClick={() => { setShowJournalDialog(false); setShowCreateDialog(true); }}
                  className="mt-4 rounded-full"
                >
                  Add First Entry
                </Button>
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  Your professional journey this summer - {myJournal.length} entries
                </p>
                {myJournal.map((entry) => {
                  const category = getCategoryInfo(entry.category);
                  return (
                    <Card key={entry.id} className="p-4 rounded-2xl">
                      <div className="flex items-start justify-between mb-2">
                        <Badge className={`${category.color} text-white`}>
                          {category.icon} {category.label}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{formatDate(entry.created_at)}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-xl">{entry.mood}</span>
                        <p className="text-sm text-foreground/80 flex-1">{entry.content}</p>
                      </div>
                      <div className="mt-2 pt-2 border-t flex items-center gap-2 text-xs text-muted-foreground">
                        <Heart className="w-3 h-3" />
                        {entry.likes?.length || 0} likes
                      </div>
                    </Card>
                  );
                })}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <style jsx>{`
        .line-clamp-4 {
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </>
  );
};

export default WeeklyHighlights;
